// src/hooks/useGlow.js

import { useState, useEffect, useMemo } from 'react';

// --- Helper Function ---
// Convierte un color hexadecimal (ej. #FFD700) a un string RGB (ej. "255, 215, 0")
// Esto es necesario para el color de la sombra del texto en formato rgba().
const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : null;
};


// --- Valores por Defecto ---
const DEFAULT_GRADIENT = 'linear-gradient(to bottom, #FFEC8B, #FFD700, #DAA520, #B8860B)';
const DEFAULT_INTENSITY_MIN = 0.2;
const DEFAULT_INTENSITY_MAX = 0.4;
const DEFAULT_INTERVAL = 3000;

/**
 * Un custom hook para crear un efecto de brillo animado en un texto.
 * @param {object} options - Un objeto de configuración opcional.
 * @param {string} options.gradient - El gradiente CSS para el fondo del texto.
 * @param {number} options.intensityMin - La intensidad mínima del brillo.
 * @param {number} options.intensityMax - La intensidad máxima del brillo.
 * @param {number} options.intervalDuration - La duración de la animación en ms.
 * @returns {object} Un objeto de estilo que se puede aplicar directamente a un elemento.
 */
export const useGlow = (options = {}) => {
    // Desestructuramos las opciones con valores por defecto
    const {
        gradient = DEFAULT_GRADIENT,
        intensityMin = DEFAULT_INTENSITY_MIN,
        intensityMax = DEFAULT_INTENSITY_MAX,
        intervalDuration = DEFAULT_INTERVAL
    } = options;

    const [glowIntensity, setGlowIntensity] = useState(intensityMin);

    // Lógica para animar la intensidad del brillo
    useEffect(() => {
        const animateGlow = () => {
            setGlowIntensity(prev => (prev === intensityMin ? intensityMax : intensityMin));
        };
        const glowInterval = setInterval(animateGlow, intervalDuration);
        return () => clearInterval(glowInterval);
    }, [intensityMin, intensityMax, intervalDuration]);

    // Lógica para extraer el color del brillo del gradiente
    const glowColorRgb = useMemo(() => {
        // Busca el primer color hexadecimal en el string del gradiente
        const match = gradient.match(/#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})/);
        if (match && match[0]) {
            const rgb = hexToRgb(match[0]);
            if (rgb) return rgb;
        }
        // Si no encuentra un color válido, usa el dorado por defecto
        return '255, 215, 0';
    }, [gradient]);

    // Objeto de estilo final
    const glowStyle = {
        fontFamily: '"Cinzel Decorative", serif',
        backgroundImage: gradient,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        textShadow: `0 0 8px rgba(${glowColorRgb}, ${glowIntensity}), 0 0 12px rgba(${glowColorRgb}, ${glowIntensity / 2})`,
        transition: 'text-shadow 1.5s ease-in-out'
    };

    return glowStyle;
};